import api from "../config/api";
import type { ApiResponse } from "../types";

export interface RevenueData {
  date?: string;
  month?: string;
  revenue: {
    LAK: number;
    THB: number;
    USD: number;
  };
  receiptCount: number;
}

export interface PaymentMethods {
  cash: { LAK: number; THB: number; USD: number };
  transfer: { LAK: number; THB: number; USD: number };
  qr: { LAK: number; THB: number; USD: number };
  card: { LAK: number; THB: number; USD: number };
}

export interface PaymentStatus {
  paid: number;
  partial: number;
  pending: number;
}

export interface RevenueReport {
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalReceipts: number;
    totalRevenue: {
      LAK: number;
      THB: number;
      USD: number;
    };
    paymentMethods: PaymentMethods;
    paymentStatus: PaymentStatus;
  };
  groupedData: RevenueData[];
}

export interface RoomUsageData {
  room: {
    _id: string;
    roomNumber: string;
    roomName: string;
    roomType: string;
  };
  totalServices: number;
  totalHours: number;
  revenue: {
    LAK: number;
    THB: number;
    USD: number;
  };
  utilizationRate: number;
}

export interface RoomUsageReport {
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalRooms: number;
    totalServices: number;
    totalUsageHours: number;
    averageUtilization: number;
  };
  roomUsage: RoomUsageData[];
}

export interface EmployeePerformanceData {
  employee: {
    _id: string;
    name: string;
    employeeId: string;
    role: string;
  };
  totalServices: number;
  totalHours: number;
  totalOvertimeHours: number;
  customerSatisfactionAvg: number;
  uniqueCustomers: number;
}

export interface EmployeePerformanceReport {
  period: {
    startDate: string;
    endDate: string;
  };
  performance: EmployeePerformanceData[];
}

export interface CustomerAnalysisData {
  newCustomers: number;
  activeCustomers: number;
  payingCustomers: number;
  totalRevenue: number;
  averageSpending: number;
  topCustomers: Array<{
    customer: {
      _id: string;
      name: string;
      customerId: string;
    };
    totalSpent: number;
    visitCount: number;
  }>;
}

export interface CustomerAnalysisReport {
  period: {
    startDate: string;
    endDate: string;
  };
  customerAnalysis: CustomerAnalysisData;
}

export interface CommissionData {
  totalEmployees: number;
  totalCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  paidCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  unpaidCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  commissionByType: {
    service: { LAK: number; THB: number; USD: number };
    referral: { LAK: number; THB: number; USD: number };
    overtime: { LAK: number; THB: number; USD: number };
  };
  employeeBreakdown: Array<{
    employee: {
      _id: string;
      name: string;
      employeeId: string;
      role: string;
    };
    totalCommission: {
      LAK: number;
      THB: number;
      USD: number;
    };
    serviceCommission: {
      LAK: number;
      THB: number;
      USD: number;
    };
    referralCommission: {
      LAK: number;
      THB: number;
      USD: number;
    };
    overtimeCommission: {
      LAK: number;
      THB: number;
      USD: number;
    };
    isPaid: boolean;
    paidAt?: string;
  }>;
}

export interface CommissionReport {
  month: number;
  year: number;
  commissionSummary: CommissionData;
}

export interface RevenueReportParams {
  startDate: string;
  endDate: string;
  groupBy?: 'day' | 'month';
}

export interface RoomUsageReportParams {
  startDate: string;
  endDate: string;
}

export interface EmployeePerformanceReportParams {
  startDate: string;
  endDate: string;
  role?: string;
}

export interface CustomerAnalysisReportParams {
  startDate: string;
  endDate: string;
}

export interface CommissionReportParams {
  month: number;
  year: number;
}

export const reportsService = {
  getRevenueReport: async (params: RevenueReportParams): Promise<ApiResponse<RevenueReport>> => {
    const response = await api.get('/reports/revenue', { params });
    return response.data;
  },

  getRoomUsageReport: async (params: RoomUsageReportParams): Promise<ApiResponse<RoomUsageReport>> => {
    const response = await api.get('/reports/room-usage', { params });
    return response.data;
  },

  getEmployeePerformanceReport: async (params: EmployeePerformanceReportParams): Promise<ApiResponse<EmployeePerformanceReport>> => {
    const response = await api.get('/reports/employee-performance', { params });
    return response.data;
  },

  getCustomerAnalysisReport: async (params: CustomerAnalysisReportParams): Promise<ApiResponse<CustomerAnalysisReport>> => {
    const response = await api.get('/reports/customer-analysis', { params });
    return response.data;
  },

  getCommissionReport: async (params: CommissionReportParams): Promise<ApiResponse<CommissionReport>> => {
    const response = await api.get('/reports/commission-summary', { params });
    return response.data;
  },
};